import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { merge, Subject } from "rxjs";
import { skip, takeUntil } from "rxjs/operators";

import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";

@Component({
    selector: "fuse-navigation",
    templateUrl: "./navigation.component.html",
    styleUrls: ["./navigation.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FuseNavigationComponent implements OnInit {
    @Input()
    layout = "vertical";

    @Input()
    navigation: any;

    // Private
    private _unsubscribeAll: Subject<any>;
    private roleAccess: any;

    /**
     *
     * @param {ChangeDetectorRef} _changeDetectorRef
     * @param {FuseNavigationService} _fuseNavigationService
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Load the navigation either from the input or from the service
        this.navigation =
            this.navigation ||
            this._fuseNavigationService.getCurrentNavigation();

        // Subscribe to the current navigation changes
        this._fuseNavigationService.onNavigationChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Load the navigation
                this.navigation =
                    this._fuseNavigationService.getCurrentNavigation();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Structural changes (add/remove): re-apply permission rules from session storage.
        merge(
            this._fuseNavigationService.onNavigationItemAdded,
            this._fuseNavigationService.onNavigationItemRemoved,
        )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.CheckingPermissions();
                this._changeDetectorRef.markForCheck();
            });

        // Item property updates (hidden, url, etc.): refresh view only.
        // Do not call CheckingPermissions() here — it calls updateNavigationItem() in a loop and
        // each update emits onNavigationItemUpdated again, causing unbounded recursion / stack overflow.
        this._fuseNavigationService.onNavigationItemUpdated
            .pipe(skip(1), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });
    }

    CheckingPermissions() {
        var _allProducts = sessionStorage.getItem("allProducts");
        if (_allProducts) {
            var allClients = JSON.parse(_allProducts);
            for (let i = 0; i < allClients.length; i++) {
                if (allClients[i]["isSubscribed"]) {
                    this._fuseNavigationService.updateNavigationItem(
                        allClients[i]["clientId"].toLowerCase(),
                        {
                            url: allClients[i]["clientUri"],
                            hidden: false,
                        },
                    );
                } else {
                    this._fuseNavigationService.updateNavigationItem(
                        allClients[i]["clientId"].toLowerCase(),
                        {
                            url: allClients[i]["clientUri"],
                            hidden: true,
                        },
                    );
                }
            }
        }

        var _userProducts = sessionStorage.getItem("roleAccessPermissions");
        if (_userProducts) {
            this.roleAccess = JSON.parse(_userProducts);
            this._fuseNavigationService.updateNavigationItem("dashboard", {
                hidden: !this.roleAccess.DashBoardAccess,
            });
            this._fuseNavigationService.updateNavigationItem(
                "case-management",
                {
                    hidden: !this.roleAccess.CaseManagementAccess,
                },
            );
            this._fuseNavigationService.updateNavigationItem("milanacalendar", {
                hidden: !this.roleAccess.CalendarAccess,
            });
            this._fuseNavigationService.updateNavigationItem("Administrators", {
                hidden: !this.roleAccess.SystemAdministrator,
            });
            this._fuseNavigationService.updateNavigationItem("autodocket", {
                hidden: !this.roleAccess.AutoDocketAccess,
            });
            this._fuseNavigationService.updateNavigationItem(
                "milanaquickdates",
                {
                    hidden: !(
                        this.roleAccess.QuickDateCalculatorAccess ||
                        this.roleAccess.QuickDatesAccess
                    ),
                },
            );
            this._fuseNavigationService.updateNavigationItem(
                "attorneybarsearch",
                {
                    hidden: !(
                        this.roleAccess.AttorneyAdmissionsAccess ||
                        this.roleAccess.ECFCredentialsAccess
                    ),
                },
            );
            this._fuseNavigationService.updateNavigationItem("cr-management", {
                hidden: !this.roleAccess.CourtRulesReportAccess,
            });
            this._fuseNavigationService.updateNavigationItem("docketalerts", {
                hidden: false,
            });
            this._fuseNavigationService.updateNavigationItem("applications", {
                hidden: false,
            });
            this._fuseNavigationService.updateNavigationItem(
                "information-center",
                {
                    hidden: !this.roleAccess.InformationCenterAccess,
                },
            );
        }
    }
}
